import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AppShell } from '../components/AppShell';
import { SettingsLogoSection, SettingsCompanySection, SettingsSignatureSection } from '../components/settings/SettingsSections';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Gear, FloppyDisk, ArrowLeft, CheckCircle, DownloadSimple } from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export function SettingsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  const isAdmin = user?.role === 'admin';
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [settings, setSettings] = useState({
    company_name: '', address: '', phone: '', email: '',
    siret: '', logo_url: '', logo_file_id: '',
    signatory_name: '', signatory_role: ''
  });
  const [hasChanges, setHasChanges] = useState(false);
  const [initialSettings, setInitialSettings] = useState(null);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/settings`, { withCredentials: true });
      if (res.data) {
        const data = res.data.data || res.data;
        const loaded = {
          company_name: data.company_name || '', address: data.address || '',
          phone: data.phone || '', email: data.email || '', siret: data.siret || '',
          logo_url: data.logo_url || '', logo_file_id: data.logo_file_id || '',
          signatory_name: data.signatory_name || '', signatory_role: data.signatory_role || ''
        };
        setSettings(loaded);
        setInitialSettings(loaded);
      }
    } catch (error) {
      toast.error('Erreur lors du chargement des paramètres');
    } finally { setLoading(false); }
  }, [setSettings, setInitialSettings, setLoading]);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const handleChange = (field, value) => {
    const updated = { ...settings, [field]: value };
    setSettings(updated);
    setHasChanges(JSON.stringify(updated) !== JSON.stringify(initialSettings));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, settings, { withCredentials: true });
      setInitialSettings({ ...settings });
      setHasChanges(false);
      toast.success('Paramètres enregistrés');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally { setSaving(false); }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Fichier trop volumineux (max 5 Mo)'); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/settings/logo`, formData,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      setSettings(prev => ({ ...prev, logo_url: res.data.logo_url || '', logo_file_id: res.data.logo_file_id || '' }));
      setHasChanges(true);
      toast.success('Logo charge');
    } catch (error) {
      toast.error('Erreur upload logo');
    } finally { setUploading(false); }
  };

  const handleDeleteLogo = async () => {
    try {
      await axios.delete(`${API}/settings/logo`, { withCredentials: true });
      setSettings(prev => ({ ...prev, logo_url: '', logo_file_id: '' }));
      setHasChanges(true);
      toast.success('Logo supprimé');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const logoSrc = settings.logo_url
    ? (settings.logo_url.startsWith('http') ? settings.logo_url : `${API}/files/${settings.logo_file_id}`)
    : null;

  if (loading) {
    return <AppShell><div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="w-10 h-10 animate-spin text-[#0055FF]" /></div></AppShell>;
  }

  return (
    <AppShell>
      <div className="space-y-6 max-w-2xl mx-auto" data-testid="settings-page">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" className="h-10 w-10 md:hidden"
            onClick={() => navigate('/dashboard')} data-testid="back-btn">
            <ArrowLeft size={22} />
          </Button>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-[#111827] flex items-center gap-2">
              <Gear size={28} className="text-[#0055FF]" /> Paramètres
            </h1>
            <p className="text-[#4B5563] mt-1">Personnalisez vos rapports et documents</p>
          </div>
        </div>

        <SettingsLogoSection logoSrc={logoSrc} uploading={uploading} fileInputRef={fileInputRef}
          onLogoUpload={handleLogoUpload} onDeleteLogo={handleDeleteLogo} />

        <SettingsCompanySection settings={settings} onChange={handleChange} />

        <SettingsSignatureSection settings={settings} onChange={handleChange} />

        {/* Save Button */}
        <div className="sticky bottom-20 md:bottom-4 z-30">
          <Button onClick={handleSave} disabled={saving || !hasChanges}
            className={`w-full h-14 text-lg font-semibold transition-all ${hasChanges ? 'bg-[#0055FF] hover:bg-blue-700 text-white shadow-lg' : 'bg-gray-200 text-gray-500'}`}
            data-testid="save-settings-btn">
            {saving ? (<><Loader2 className="mr-2 h-5 w-5 animate-spin" />Enregistrément...</>)
              : hasChanges ? (<><FloppyDisk size={22} className="mr-2" />Enregistrér les modifications</>)
              : (<><CheckCircle size={22} className="mr-2" />Paramètres a jour</>)}
          </Button>
        </div>

        <div className="text-center text-xs text-[#4B5563] pb-4">
          <p>Ces paramètres sont utilisés pour personnaliser vos rapports et documents.</p>
        </div>

        {isAdmin && (
          <Card data-testid="settings-presentation-section">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <DownloadSimple size={22} className="text-[#FF6B00]" />
                Documents de presentation
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-[#4B5563]">Telechargez les plaquettes commerciales CHANTIX personnalisees avec vos coordonnees.</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button variant="outline" className="flex-1 h-12" onClick={() => navigate('/presentation')} data-testid="settings-go-presentation-btn">
                  <DownloadSimple size={20} className="mr-2 text-[#FF6B00]" /> Acceder aux presentations
                </Button>
                <Button variant="outline" className="flex-1 h-12" onClick={() => navigate('/admin/plans')} data-testid="settings-go-plans-btn">
                  <DownloadSimple size={20} className="mr-2 text-blue-600" /> Gestion des abonnements
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </AppShell>
  );
}
